/**
 * RTWO Blockchain Core Foundation — Protocol Block Repository Adapter
 * RTWO v2.2 | Module ID: RTWO-BLCK-001
 *
 * Maps protocol engine blocks to the blockchain core read layer.
 * Only exposes blocks with real hash and height — no placeholders.
 */

import type { BlockRepository } from '../../blockchain-protocol-engine/repositories/protocol-repositories';
import type { RTWOBlock } from '../../blockchain-protocol-engine/domain-models';
import type { BlockchainCoreBlock } from '../domain-models';
import type { IBlockchainCoreBlockRepository } from './block-repository-interface';

function isRealBlock(block: RTWOBlock): boolean {
  return (
    block.blockHash !== null && block.blockHash.trim().length > 0 && block.blockNumber !== null
  );
}

function mapProtocolBlock(block: RTWOBlock): BlockchainCoreBlock | null {
  if (!isRealBlock(block)) {
    return null;
  }

  return {
    blockId: block.blockHash!,
    blockNumber: block.blockNumber!.toString(),
    blockHash: block.blockHash,
    parentHash: block.parentHash,
    timestamp: block.timestamp,
    transactionCount: block.transactionCount,
    proposer: block.proposer,
    source: 'Blockchain Protocol Engine — Block Repository',
  };
}

export class ProtocolBlockRepositoryAdapter implements IBlockchainCoreBlockRepository {
  constructor(private readonly protocolRepository: BlockRepository) {}

  isBackendConnected(): boolean {
    return true;
  }

  findLatest(): BlockchainCoreBlock | null {
    const latest = this.protocolRepository.findLatest();
    if (!latest) {
      return null;
    }
    return mapProtocolBlock(latest);
  }

  findByNumber(blockNumber: bigint): BlockchainCoreBlock | null {
    const match = this.protocolRepository
      .findAll()
      .find((block) => block.blockNumber === blockNumber);
    if (!match) {
      return null;
    }
    return mapProtocolBlock(match);
  }

  findByHash(blockHash: string): BlockchainCoreBlock | null {
    const match = this.protocolRepository.findByHash(blockHash);
    if (!match) {
      return null;
    }
    return mapProtocolBlock(match);
  }

  count(): number {
    return this.protocolRepository.findAll().filter(isRealBlock).length;
  }
}
